import localforage from 'localforage';
import cloneDeep from 'lodash/cloneDeep';
import { ref } from 'valtio';

import type { RequireAtLeastOne } from '@/utils/tsutils';

import type { OrderAppDeliveryMode } from '@/graphql/orderApp/api';
import type { CheckoutFragment } from '@/graphql/saleor/api';
import { CacheKey } from '@/lib/const';
import type { ApiError, ApiErrorType } from '@/lib/types';

import type { CheckoutState } from './checkoutState';
import { checkoutState, initialState } from './checkoutState';

export const checkoutActions = {
  updateProcessingState: ({
    clearErrors = true,
    initializing,
    processing,
  }: RequireAtLeastOne<
    { initializing: boolean; processing: boolean } & { clearErrors?: boolean }
  >) => {
    if (processing !== undefined && processing !== checkoutState.isProcessing) {
      checkoutState.isProcessing = processing;
    }

    if (
      initializing !== undefined &&
      initializing !== checkoutState.isInitializing
    ) {
      checkoutState.isInitializing = initializing;
    }

    if (clearErrors) {
      checkoutActions.clearErrors();
    }
  },

  updateDeliveryMethods: (deliveryMethods: Array<OrderAppDeliveryMode>) => {
    checkoutState.deliveryMethods = deliveryMethods;
  },

  updateCheckout: (checkout: Partial<CheckoutFragment>) => {
    checkoutState.checkout = {
      ...(checkoutState.checkout ?? {}),
      ...checkout,
    } as CheckoutFragment;
  },

  clearCheckoutLines: () => {
    checkoutState.checkout = {
      ...(checkoutState.checkout ?? {}),
      lines: [],
    } as CheckoutFragment;
  },

  setErrors: (errors: ApiError[], errorType: ApiErrorType) => {
    checkoutState.errors = errors.map((error) => ({
      type: errorType,
      ...error,
    })) as CheckoutState['errors'];
  },

  getErrors: () => {
    return checkoutState.errors;
  },

  clearErrors: () => {
    checkoutState.errors = [];
  },

  // https://breitling.atlassian.net/browse/BEC-3287
  notify: (notification: CheckoutState['notification']) => {
    checkoutState.notification = notification;
  },

  setGatewayCheckout: (checkout: CheckoutState['gatewayCheckout']) => {
    checkoutState.gatewayCheckout = checkout ? ref(checkout) : null;
  },

  setGatewaysConfig: (configs: CheckoutState['gatewaysConfig']) => {
    checkoutState.gatewaysConfig = configs;
  },

  setPaymentMethod: (opts: Partial<CheckoutState['paymentMethod']>) => {
    checkoutState.paymentMethod = {
      ...checkoutState.paymentMethod,
      ...opts,
    } as CheckoutState['paymentMethod'];
  },

  setOrderId: (id: CheckoutState['orderId']) => {
    checkoutState.orderId = id ?? '';
  },

  setVoucherType: (type: CheckoutState['voucherType']) => {
    checkoutState.voucherType = type ?? '';
  },
  setIsChangedAfterOrderCreated: (value: boolean) => {
    checkoutState.isChangedAfterOrderCreated = value;
  },

  clear: async () => {
    const resetObj = cloneDeep({
      ...initialState,
      isInitializing: false,
    });
    // valtio.pmnd.rs/docs/how-tos/how-to-reset-state
    Object.keys(resetObj).forEach((key) => {
      (checkoutState as any)[key] = resetObj[key as keyof CheckoutState];
    });

    await localforage.removeItem(CacheKey.CheckoutId);
    await localforage.removeItem(CacheKey.OrderId);
    await localforage.removeItem(CacheKey.IsChangedAfterOrderCreated);
  },
};
